// ThreadList.tsx — 1.24.00 C2: the rest of the thread, under the hero post.
//
// Shown on the ROOT's page (its parts, in order) and on a PART's page (the root
// plus the sibling parts). Either way the post already in the hero is skipped —
// nobody needs to read the same post twice on one screen.
//
// Numbering comes from each part's stored `thread.position`, never from its
// index in this list: positions are assigned once at create and never
// renumbered, so a deleted middle part leaves "Part 4" sitting next to "Part 2"
// rather than quietly renumbering what someone wrote.
//
// A thread whose ROOT is gone (deleted by its author, or removed by the
// Council) still lists everything under it — the parts are ordinary posts and
// nothing about the root's removal touched them. A tombstone stands in the
// root's slot; see PostThreadResponse for why the two reasons are not named.

import styled from 'styled-components';
import type { Post } from '@/core/models/post';
import { PostCard } from '@/components/PostCard';
import { Icon } from '@/icons';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Head = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Entry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PartLabel = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

// The same dashed-border tombstone QuoteEmbed shows for a quoted post the
// viewer can no longer see — one visual language for "the thing that was here
// is gone", rather than a second one invented for threads.
const Unavailable = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px dashed ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

export interface ThreadListProps {
  /** The thread's first post — null while loading, and when it is gone. */
  root: Post | null;
  /**
   * The root is gone but the thread is not: the server answered 200 with
   * `root: null` and the parts that survived it. A tombstone takes the root's
   * place. Never inferred from `root === null` on its own, which is also what
   * the caller holds while the request is in flight.
   */
  rootUnavailable: boolean;
  /** Parts in position order, as the server returned them. */
  parts: Post[];
  /** The post currently in the hero — skipped in the list. */
  currentPostId: string;
}

export function ThreadList({ root, rootUnavailable, parts, currentPostId }: ThreadListProps) {
  // The server's denormalized count (carried on every part) is the authority —
  // `parts.length` under-counts a thread whose middle part was deleted, and the
  // list would then disagree with the "Part 2 of 5" chip on the very same card.
  const anyThread = parts.find((p) => p.thread)?.thread;
  const total = anyThread?.count ?? 1 + parts.length;
  // The gone root can still be the post in the hero: a soft-deleted post opens
  // on its own page and says "Deleted by author" there. A second tombstone
  // under it would contradict the card right above it — the same reason the
  // root's own card is skipped when it is the hero. `thread.rootId` is carried
  // by every part, which is the only place the gone root's id survives.
  const showRootTombstone = rootUnavailable && anyThread?.rootId !== currentPostId;
  const entries = [
    ...(root ? [{ post: root, position: 1 }] : []),
    ...parts.map((p, i) => ({ post: p, position: p.thread?.position ?? i + 2 })),
  ].filter((e) => e.post.id !== currentPostId);

  if (entries.length === 0 && !showRootTombstone) return null;

  return (
    <Wrap aria-label="Thread" data-testid="thread-list">
      <Head>This thread</Head>
      {showRootTombstone && (
        <Entry>
          <PartLabel>Part 1 of {total}</PartLabel>
          <Unavailable data-testid="thread-root-unavailable">
            <Icon name="ban" size={16} />
            The post that started this thread is no longer available.
          </Unavailable>
        </Entry>
      )}
      {entries.map((e) => (
        <Entry key={e.post.id}>
          <PartLabel>
            Part {e.position} of {total}
          </PartLabel>
          <PostCard post={e.post} />
        </Entry>
      ))}
    </Wrap>
  );
}
